import React, { useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../../utils/api';

const normalizeCity = (city) => ({
  id: city.city_id ?? city.id ?? city.name,
  name: city.name ?? city.city_name ?? 'City',
});

const LocationSelector = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const debounceRef = useRef(null);

  const fetchCities = async (query = '') => {
    setLoading(true);
    try {
      const qs = query.trim() ? `?search=${encodeURIComponent(query.trim())}` : '';
      const response = await fetch(buildApiUrl(`/api/v1/customer/cities${qs}`), {
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.message || 'Failed to load cities');
      setCities((json.data || []).map(normalizeCity));
    } catch {
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setSearch('');
    }, 150);
  };

  useEffect(() => {
    if (open && cities.length === 0) fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCities(search), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, open]);

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (cityName) => {
    onChange(cityName);
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={[
          'flex min-w-[160px] items-center gap-2.5 rounded-2xl px-4 py-2.5 text-left transition-all duration-200',
          open
            ? 'bg-primary-50 shadow-lg shadow-primary-500/10 ring-2 ring-primary-500/30'
            : 'bg-gray-50 hover:bg-white hover:shadow-md hover:ring-1 hover:ring-gray-200',
        ].join(' ')}
      >
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${open ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-600'}`}>
          <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0Z" />
            <circle cx="12" cy="11" r="3" />
          </svg>
        </div>

        <div className="flex flex-col leading-none">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary-600">
            Deliver to
          </span>
          <span className="mt-0.5 text-sm font-bold text-gray-800">
            {value || 'Select city'}
          </span>
        </div>

        <svg className={`ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-[280px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-black/10">
          <div className="flex items-center gap-2.5 border-b border-gray-100 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search city..."
              className="flex-1 border-none bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-0"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="text-gray-300 transition-colors hover:text-gray-500">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto p-3">
            {loading ? (
              <div className="py-6 text-center">
                <div className="mx-auto h-7 w-7 animate-spin rounded-full border-4 border-gray-100 border-t-primary-600" />
                <p className="mt-2 text-xs font-semibold text-gray-400">Loading cities</p>
              </div>
            ) : cities.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">No cities found</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {cities.map((city) => {
                  const isActive = city.name === value;
                  return (
                    <button
                      key={city.id}
                      type="button"
                      onClick={() => handleSelect(city.name)}
                      className={[
                        'flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold transition-all duration-150',
                        isActive
                          ? 'bg-primary-600 text-white shadow-md shadow-primary-600/30'
                          : 'bg-gray-50 text-gray-700 hover:bg-primary-50 hover:text-primary-700 hover:ring-1 hover:ring-primary-200',
                      ].join(' ')}
                    >
                      <svg className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-white/80' : 'text-primary-500'}`} fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 0 : 2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0Z" />
                        <circle cx="12" cy="11" r="3" fill={isActive ? 'currentColor' : 'none'} />
                      </svg>
                      {city.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
